import { FC, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "react-query";
import client from "src/api";
import { getErrorMessage } from "src/lib/utils";
import GlobalContext from "src/context/global-context";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import useAuth from "src/hooks/use-auth";
import Button from "src/components/ui/button";
import Password from "src/components/ui/password";
import { LockIcon } from "src/components/icons/lock-icon";

const schema = yup
  .object({
    oldPassword: yup
      .string()
      .required("Old password is required.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        "Password must be at least 8 characters long, include 1 uppercase letter, 1 special character and 1 number."
      ),
    newPassword: yup
      .string()
      .required("New password is required.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        "Password must be at least 8 characters long, include 1 uppercase letter, 1 special character and 1 number."
      ),
    confirmNewPassword: yup
      .string()
      .required("Confirm password is required.")
      .oneOf([yup.ref("newPassword")], "Passwords do not match"),
  })
  .required();

type FormData = yup.InferType<typeof schema>;

const ProfileChangePasswordPage: FC = () => {
  const { unauthorize } = useAuth();
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const [oldPasswordVal, newPasswordVal, confirmNewPasswordVal] = watch([
    "oldPassword",
    "newPassword",
    "confirmNewPassword",
  ]);

  const { mutate: changePassword, isLoading: isChangePasswordLoading } =
    useMutation(client.auth.changePassword, {
      onSuccess: async (data: any) => {
        const message = data?.message || "Password changed successfully.";
        setAlertText(message);
        setIsAlertOpened(true);

        setIsLoggingOut(true);
        try {
          await client.auth.logout();
        } catch (logoutError) {
          console.error("Logout after password change failed:", logoutError);
        } finally {
          unauthorize();
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response?.data) {
          setAlertText(getErrorMessage(error.response.data));
        } else if (error.message) {
          setAlertText(error.message);
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    });

  const isLoading = isChangePasswordLoading || isLoggingOut;

  const onSubmit = (data: FormData) => {
    changePassword({
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
      confirmNewPassword: data.confirmNewPassword,
    });
  };

  const isSubmitDisabled =
    isLoading ||
    !oldPasswordVal ||
    !newPasswordVal ||
    !confirmNewPasswordVal ||
    Boolean(errors.oldPassword || errors.newPassword || errors.confirmNewPassword);

  return (
    <div className="w-full h-full flex-col p-2 sm:p-4">
      <div className="w-full flex flex-row items-center justify-between mb-4">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Change Password
        </p>
      </div>

      <div className="flex flex-col bg-white rounded-[12px] p-4 sm:p-6 gap-4 max-w-[640px] shadow-sm">
        <div className="w-full flex flex-row items-center justify-between">
          <div className="flex flex-row gap-2 items-center">
            <LockIcon className="w-[24px] h-[24px] text-primary" />
            <p className="font-manrope text-[18px] font-medium text-primary leading-[24px]">
              Change Password
            </p>
          </div>
        </div>

        <div className="w-full">
          <hr className="h-[1px] border-0 bg-[#E6E8EC] opacity-50 dark:opacity-50" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <Password
            label="Old Password"
            id="old-password"
            autoComplete="off"
            placeholder="Enter your Old password"
            className="w-full mt-2"
            {...register("oldPassword")}
            error={errors.oldPassword && (errors.oldPassword.message as string)}
          />

          <Password
            label="Password"
            id="new-password"
            autoComplete="off"
            placeholder="Enter your password"
            className="w-full mt-4"
            {...register("newPassword")}
            error={errors.newPassword && (errors.newPassword.message as string)}
          />

          <Password
            label="Confirm Password"
            id="confirm-password"
            autoComplete="off"
            placeholder="Confirm your password"
            className="w-full mt-4"
            {...register("confirmNewPassword")}
            error={
              errors.confirmNewPassword &&
              (errors.confirmNewPassword.message as string)
            }
          />

          <Button
            variant="solid"
            type="submit"
            className="mt-6 rounded-[10px] font-manrope text-[16px] font-bold leading-[24px] h-[64px] w-full bg-active hover:bg-main sm:max-w-[215px]"
            isLoading={isLoading}
            disabled={isSubmitDisabled}
          >
            Update Password
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfileChangePasswordPage;
